import { LayoutTemplate } from "../LayoutTemplate";
import { LineChart } from "@mui/x-charts";
import { formatMoneyBRL } from "../../../utils/masks";
import { reverseToBrDate } from "../../../utils/dateTransform";
import { useFinancialProductionReport } from "./useFinancialProductionReport";
import {
  Breakdown,
  ChartLegend,
  ReportFilters,
  card,
  chartSx,
  num,
  pct,
} from "../GeneralTemplate/ui";

const money = (v?: number) => formatMoneyBRL(v || 0);
const CHANNELS = [
  { label: "Loja", color: "#1D1D1B" },
  { label: "Móvel", color: "#E84E1B" },
  { label: "Itinerante", color: "#F4A988" },
];

export const FinancialProductionReportTemplate = () => {
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    dataFilter,
    handleFilter,
    dataFinanceira,
    axisLinear,
    dataLinear,
    CidadeOptions,
    GraphColors,
  } = useFinancialProductionReport();

  const lojas = dataFinanceira?.lojas || [];
  const sum = (key: string) =>
    lojas.reduce((acc, item) => acc + (item[key] || 0), 0);

  return (
    <LayoutTemplate title="Relatório de Produção Financeira">
      <div className="flex flex-col gap-5 text-[#1D1D1B]">
        <span className="text-sm text-[#6B6B66]">
          {reverseToBrDate(dataFilter.dataInicio)} –{" "}
          {reverseToBrDate(dataFilter.dataFim)} ·{" "}
          {dataFilter.empresa || "Todas as empresas"}
        </span>

        <ReportFilters
          control={control}
          setValue={setValue}
          watch={watch}
          onSubmit={handleSubmit(handleFilter)}
          options={CidadeOptions}
          colors={GraphColors}
        />

        {/* KPIs principais */}
        <section
          aria-label="Indicadores principais"
          className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4"
        >
          <div className="flex flex-col gap-3.5 rounded-2xl bg-[#1D1D1B] p-5 text-white">
            <span className="text-[13px] font-medium text-[#C9C8C2]">
              Faturamento total
            </span>
            <span className="text-4xl font-bold tabular-nums tracking-tight">
              {money(dataFinanceira?.faturamentoTotal)}
            </span>
            <span className="text-[13px] text-[#C9C8C2]">
              no período selecionado
            </span>
          </div>
          {[
            {
              title: "Ticket médio",
              value: money(dataFinanceira?.ticketMedio),
              hint: "por vistoria",
            },
            {
              title: "Vistorias R$ 152,00",
              value: num(dataFinanceira?.qtd152),
              hint: "quantidade",
            },
            {
              title: "Vistorias R$ 190,00",
              value: num(dataFinanceira?.qtd190),
              hint: "quantidade",
            },
          ].map((k) => (
            <div key={k.title} className={`${card} flex flex-col gap-3.5`}>
              <span className="text-[13px] font-medium text-[#6B6B66]">
                {k.title}
              </span>
              <span className="text-4xl font-bold tabular-nums tracking-tight">
                {k.value}
              </span>
              <span className="text-[13px] text-[#6B6B66]">{k.hint}</span>
            </div>
          ))}
        </section>

        {/* Composição */}
        <section
          aria-label="Composição"
          className="grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-4"
        >
          <Breakdown
            title="Faturamento por canal"
            format={money}
            parts={[
              dataFinanceira?.totalLoja,
              dataFinanceira?.totalMovel,
              dataFinanceira?.totalItinerante,
            ].map((value, i) => ({ ...CHANNELS[i], value }))}
          />
          <Breakdown
            title="Faturamento por tipo de serviço"
            format={money}
            parts={[
              {
                label: "Primeiro emplacamento",
                value: dataFinanceira?.totalPrimeiroEmplacamento,
                color: "#E84E1B",
              },
              {
                label: "Transferência",
                value: dataFinanceira?.totalTransferencia,
                color: "#3C3C3B",
              },
            ]}
          />
        </section>

        {/* Gráficos */}
        <section className="flex flex-wrap gap-4">
          <div className={`${card} flex min-w-0 flex-[2_1_560px] flex-col gap-4`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-col gap-1">
                <h2 className="text-base font-semibold">Faturamento diário</h2>
                <span className="text-[13px] text-[#6B6B66]">
                  Faturamento por dia, por empresa
                </span>
              </div>
              <ChartLegend series={dataLinear} />
            </div>
            <LineChart
              height={300}
              margin={{ top: 16, right: 16, bottom: 30, left: 64 }}
              series={dataLinear}
              xAxis={[{ scaleType: "band", data: axisLinear }]}
              slotProps={{ legend: { hidden: true } }}
              sx={chartSx}
            />
          </div>

          <div className={`${card} flex min-w-0 flex-[1_1_340px] flex-col gap-[18px]`}>
            <div className="flex flex-col gap-1">
              <h2 className="text-base font-semibold">
                Mix de canal por empresa
              </h2>
              <span className="text-[13px] text-[#6B6B66]">
                % de vistorias por Loja, Móvel e Itinerante
              </span>
            </div>
            <div className="flex gap-4 text-[13px] text-[#4A4A46]">
              {CHANNELS.map((c) => (
                <span key={c.label} className="inline-flex items-center gap-1.5">
                  <span
                    className="h-2.5 w-2.5 rounded-[3px]"
                    style={{ background: c.color }}
                  />
                  {c.label}
                </span>
              ))}
            </div>
            <div className="flex flex-col gap-[18px]">
              {dataFinanceira?.empresas?.map((item) => {
                const values = [
                  item.qtdLoja,
                  item.qtdDelivery,
                  item.qtdItinerante,
                ];
                return (
                  <div key={item.empresa} className="flex flex-col gap-2">
                    <div className="flex justify-between text-sm">
                      <span className="inline-flex items-center gap-2 font-medium">
                        <span
                          className="h-2 w-2 rounded-full"
                          style={{ background: GraphColors[item.empresa] }}
                        />
                        {item.empresa}
                      </span>
                      <span className="tabular-nums text-[#6B6B66]">
                        {values.map((v) => `${v || 0}%`).join(" · ")}
                      </span>
                    </div>
                    <div className="flex h-[22px] gap-0.5 overflow-hidden rounded-md bg-[#F1F0EC]">
                      {values.map(
                        (v, i) =>
                          v > 0 && (
                            <span
                              key={CHANNELS[i].label}
                              style={{
                                flexGrow: v,
                                background: CHANNELS[i].color,
                              }}
                            />
                          )
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Tabela por unidade */}
        <section className="mb-8 flex flex-col overflow-hidden rounded-2xl border border-[#E7E6E2] bg-white">
          <div className="flex items-baseline gap-2.5 px-5 py-4">
            <h2 className="text-base font-semibold">Faturamento por unidade</h2>
            <span className="text-[13px] text-[#6B6B66]">
              {lojas.length} unidades
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1080px] border-collapse text-sm tabular-nums [&_td]:whitespace-nowrap [&_td]:px-3.5 [&_td]:py-3 [&_th]:whitespace-nowrap [&_th]:px-3.5 [&_th]:py-3">
              <thead>
                <tr className="bg-[#F7F7F5] text-right text-xs uppercase tracking-wider text-[#6B6B66] [&_th]:font-semibold">
                  <th className="text-left">Unidade</th>
                  <th className="text-left">Canal</th>
                  <th>Loja</th>
                  <th>%</th>
                  <th>Móvel</th>
                  <th>%</th>
                  <th>Itinerante</th>
                  <th>%</th>
                  <th>Ticket médio</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {lojas.map((item) => {
                  const canais =
                    (item.valorLoja || 0) +
                    (item.valorMovel || 0) +
                    (item.valorItinerante || 0);
                  return (
                    <tr
                      key={`${item.empresa}-${item.loja}`}
                      className="border-t border-[#EEEDE9] text-right hover:bg-[#FAFAF8]"
                    >
                      <td className="text-left">
                        <div className="flex items-center gap-2.5">
                          <span
                            className="h-8 w-1 rounded-sm"
                            style={{ background: GraphColors[item.empresa] }}
                          />
                          <div className="flex flex-col gap-0.5">
                            <span className="font-semibold">
                              {item.loja || "---"}
                            </span>
                            <span className="text-xs text-[#6B6B66]">
                              {item.empresa || "---"}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="flex h-2 w-24 gap-px overflow-hidden rounded-full bg-[#F1F0EC]">
                          {[
                            item.valorLoja,
                            item.valorMovel,
                            item.valorItinerante,
                          ].map((v, i) => (
                            <span
                              key={CHANNELS[i].label}
                              style={{
                                width: `${pct(v, canais)}%`,
                                background: CHANNELS[i].color,
                              }}
                            />
                          ))}
                        </div>
                      </td>
                      <td>{money(item.valorLoja)}</td>
                      <td className="text-[#6B6B66]">{item.perLoja || 0}%</td>
                      <td>{money(item.valorMovel)}</td>
                      <td className="text-[#6B6B66]">{item.perMovel || 0}%</td>
                      <td>{money(item.valorItinerante)}</td>
                      <td className="text-[#6B6B66]">
                        {item.perItinerante || 0}%
                      </td>
                      <td>{money(item.ticketMedio)}</td>
                      <td className="font-bold">{money(item.total)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-[#1D1D1B] bg-[#F7F7F5] text-right font-bold">
                  <td className="text-left">Total geral</td>
                  <td />
                  <td>{money(sum("valorLoja"))}</td>
                  <td />
                  <td>{money(sum("valorMovel"))}</td>
                  <td />
                  <td>{money(sum("valorItinerante"))}</td>
                  <td />
                  <td>{money(dataFinanceira?.ticketMedio)}</td>
                  <td>{money(sum("total"))}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="flex gap-4 border-t border-[#EEEDE9] px-5 py-3 text-xs text-[#6B6B66]">
            {CHANNELS.map((c) => (
              <span key={c.label} className="inline-flex items-center gap-1.5">
                <span
                  className="h-2 w-2.5 rounded-sm"
                  style={{ background: c.color }}
                />
                {c.label}
              </span>
            ))}
          </div>
        </section>
      </div>
    </LayoutTemplate>
  );
};
